import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Flex from 'antd/lib/flex';
import Avatar from 'antd/lib/avatar';
import Tooltip from 'antd/lib/tooltip';
import Tag from 'antd/lib/tag';
import {
    CheckCircleFilled,
    RetweetOutlined,
    ClockCircleOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat, convertTo24HourFormat, Today } from '@/timeHelpers';
import {
    displayStringForDestination,
    displayTextForOverviewDestinationStatus,
    tagColorForOverviewDestinationStatus,
    colorForWorkflowRunOverviewDisplayStatus,
    displayTextForWorkflowRunOverviewDisplayStatus,
    statusStringForReleaseStatus,
} from '@/helpers/displayHelpers';
import { statusColorForReleaseStatus } from '@/helpers';
import { antGrey, runwayColors, summaryViewColors, tagStyle, titleStyle } from '@/components/styles/globalStyles';
import { RootState } from '@/reducers/reducers';
import { selectAppTimeFormatSetting } from '@/selectors/selectors';
import {
    AppPlatform,
    AppStoreType,
    ReleaseType,
    ReleaseStatus,
    ReleaseStepStatus,
    ReleaseTimelinePhase,
    DestinationPhasedRelease,
} from '@/types';
import { OverviewDestinationStatus } from '@/types/workflowOverview';
import WorkflowsPlatformIcon from '@/components/icons/WorkflowsPlatformIcon';
import { HotfixStatusTag } from '@/components/workflowOverview/widgets/WorkflowRunOverviewStatus';
import { deriveWorkflowStatus } from '@/helpers/summaryViewHelpers';

export const EmptyCell: React.FC = () => (
    <div
        style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}
    >
        <Text style={{ color: antGrey.grey6 }}>-</Text>
    </div>
);

export const NoBuildsCellComponent: React.FC = () => {
    return (
        <div
            style={{
                backgroundColor: antGrey.grey3,
                borderRadius: 8,
                paddingLeft: 12,
                paddingRight: 12,
                paddingTop: 6,
                paddingBottom: 6,
                display: 'inline-block',
            }}
        >
            <Text style={{ color: antGrey.grey7, fontSize: 12 }}>No builds</Text>
        </div>
    );
};

interface TableDateCellProps {
    date?: string | null;
    appId: string;
}

export const TableDateCell: React.FC<TableDateCellProps> = ({ date, appId }) => {
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));

    if (!date) {
        return <Text>-</Text>;
    }

    const dateMoment = dayjs(date);
    const isComplete = Today.isAfter(dateMoment);
    const dateFormat = DatetimeFormat.ShortDayMonthDate;
    const baseTimeFormat = dateMoment.minute() !== 0 ? DatetimeFormat.ShortTime : DatetimeFormat.Hour;
    const timeFormat = use24HourTimeFormat ? convertTo24HourFormat(baseTimeFormat) : baseTimeFormat;

    const formattedDate = `${dateMoment.format(dateFormat)} / ${dateMoment.format(timeFormat)}`;

    return (
        <Space size={8} style={{ whiteSpace: 'nowrap' }}>
            {isComplete ?
                <CheckCircleFilled style={{ color: runwayColors.statusGreen, fontSize: 20 }} />
            :   <RetweetOutlined style={{ color: antGrey.grey5, fontSize: 20 }} />}
            <Text>{formattedDate}</Text>
        </Space>
    );
};

interface DurationCellProps {
    durationText: string | null;
}

export const DurationCell: React.FC<DurationCellProps> = ({ durationText }) => {
    if (!durationText) return <EmptyCell />;

    return (
        <Space align="center" size={4}>
            <ClockCircleOutlined style={{ color: antGrey.grey6 }} />
            <Text>{durationText}</Text>
        </Space>
    );
};

interface DestinationCellProps {
    destinationName: string;
    appName?: string;
    appImageUrl: string;
    platform: AppPlatform;
    appStoreType?: AppStoreType;
}

export const DestinationCell: React.FC<DestinationCellProps> = ({
    destinationName,
    appName,
    appImageUrl,
    platform,
    appStoreType,
}) => {
    const displayName = displayStringForDestination(platform, appStoreType, destinationName, appName);

    return (
        <Space size={8} style={{ whiteSpace: 'nowrap' }}>
            {!!appImageUrl || !!destinationName ?
                <Avatar
                    shape="square"
                    size={20}
                    src={appImageUrl}
                    style={{
                        borderRadius: 4,
                        flexShrink: 0,
                    }}
                >
                    {(destinationName || appName)?.charAt(0).toUpperCase()}
                </Avatar>
            :   <WorkflowsPlatformIcon platform={platform} width={20} height={20} />}
            <Text style={{ fontSize: 14 }}>{displayName}</Text>
        </Space>
    );
};

interface HealthMetricCellProps {
    value?: string;
    change?: number;
    changeStatus?: 'Good' | 'Bad' | 'Neutral';
}

const CHANGE_STATUS_COLORS = {
    Good: { background: summaryViewColors.successLight, arrow: summaryViewColors.success },
    Bad: { background: summaryViewColors.errorLight, arrow: summaryViewColors.error },
    Neutral: { background: antGrey.grey2, arrow: antGrey.grey6 },
};

export const getBackgroundColorForHealthMetric = (changeStatus?: string): string => {
    return CHANGE_STATUS_COLORS[changeStatus as keyof typeof CHANGE_STATUS_COLORS]?.background || antGrey.grey2;
};

const getArrowColorForChangeStatus = (changeStatus?: string): string => {
    return CHANGE_STATUS_COLORS[changeStatus as keyof typeof CHANGE_STATUS_COLORS]?.arrow || antGrey.grey6;
};

export const HealthMetricCell: React.FC<HealthMetricCellProps> = ({ value, change, changeStatus }) => {
    if (!value) return <EmptyCell />;

    const arrowColor = getArrowColorForChangeStatus(changeStatus);

    const renderArrow = () => {
        if (!change || change === 0) return null;
        if (changeStatus === 'Neutral') return null;

        const ArrowIcon = change > 0 ? ArrowUpOutlined : ArrowDownOutlined;
        return <ArrowIcon style={{ fontSize: 12, color: arrowColor }} />;
    };

    return (
        <Space size={4}>
            <Text style={{ fontSize: 12 }}>{value}</Text>
            {renderArrow()}
        </Space>
    );
};

// StatusCell component
interface RolloutExtraData {
    destination: {
        platform: AppPlatform;
        appStoreType: AppStoreType;
    };
    status: string;
    phasedReleaseState?: string;
    phasedReleasePercentage?: number;
}

interface StatusCellProps {
    type: ReleaseType;
    status: ReleaseStatus;
    timelinePhase: ReleaseTimelinePhase;
    phasedReleaseInfo?: DestinationPhasedRelease;
    rolloutExtraData?: RolloutExtraData;
    compact?: boolean;
}

const DUPLICATE_STATUSES = [
    OverviewDestinationStatus.Upcoming,
    OverviewDestinationStatus.Skipped,
    OverviewDestinationStatus.Inactive,
];

export const StatusCell: React.FC<StatusCellProps> = ({
    type,
    status,
    timelinePhase,
    phasedReleaseInfo,
    rolloutExtraData,
}) => {
    const isHotfix = type === 'hotfix';
    const workflowStatus = deriveWorkflowStatus(status, timelinePhase, phasedReleaseInfo);

    const workflowStatusText =
        workflowStatus ?
            displayTextForWorkflowRunOverviewDisplayStatus(workflowStatus)
        :   statusStringForReleaseStatus(status);
    const workflowStatusColor =
        workflowStatus ? colorForWorkflowRunOverviewDisplayStatus(workflowStatus) : statusColorForReleaseStatus(status);

    const destinationStatus = rolloutExtraData?.status as OverviewDestinationStatus | undefined;
    const shouldShowDestinationStatus = destinationStatus && !DUPLICATE_STATUSES.includes(destinationStatus);

    const destinationStatusText =
        shouldShowDestinationStatus ?
            displayTextForOverviewDestinationStatus(
                destinationStatus,
                rolloutExtraData.destination?.platform,
                rolloutExtraData.destination?.appStoreType,
                rolloutExtraData.phasedReleasePercentage,
            )
        :   '';

    return (
        <Flex vertical gap={4} style={{ maxWidth: 200, overflow: 'hidden' }}>
            <Flex gap={8} wrap={false}>
                <Tooltip title={`Release Status: ${workflowStatusText}`}>
                    <Tag
                        color={workflowStatusColor}
                        style={{
                            ...tagStyle,
                            marginRight: 0,
                        }}
                    >
                        {workflowStatusText}
                    </Tag>
                </Tooltip>
                {isHotfix && (
                    <Tooltip title="Hotfix">
                        <span>
                            <HotfixStatusTag shouldLightenTagContent={false} />
                        </span>
                    </Tooltip>
                )}
            </Flex>
            {shouldShowDestinationStatus && (
                <Tooltip title={`Rollout: ${destinationStatusText}`}>
                    <Tag
                        style={{
                            ...titleStyle,
                            fontSize: 10,
                            lineHeight: '22px',
                            margin: 0,
                            color: tagColorForOverviewDestinationStatus(destinationStatus),
                            background: runwayColors.white,
                            border: `1px solid ${tagColorForOverviewDestinationStatus(destinationStatus)}`,
                            maxWidth: 'fit-content',
                        }}
                    >
                        {destinationStatusText}
                    </Tag>
                </Tooltip>
            )}
        </Flex>
    );
};

export const renderColumnTitle = (title: string, iconSrc?: string, iconAlt?: string) => {
    if (iconSrc) {
        return (
            <Space size={4}>
                <img src={iconSrc} alt={iconAlt} width={20} height={20} />
                <Text style={{ fontSize: 12, color: antGrey.grey7, fontWeight: 400 }}>{title}</Text>
            </Space>
        );
    }
    return <Text style={{ fontSize: 12, color: antGrey.grey7, fontWeight: 400 }}>{title}</Text>;
};

// Release Step Cell
export interface ReleaseStepCellData {
    stepRunId: string;
    stepConfigId: string;
    name: string;
    type: string;
    status: ReleaseStepStatus;
    statusReasonString: string;
    pendingChecklistItemCount: number;
}

export const lightBackgroundColorForStatus = (status: ReleaseStepStatus): string => {
    switch (status) {
        case ReleaseStepStatus.Ready:
            return summaryViewColors.successLight;
        case ReleaseStepStatus.Pending:
        case ReleaseStepStatus.PendingChecklistOnly:
            return summaryViewColors.warningLight;
        case ReleaseStepStatus.Blocked:
            return summaryViewColors.errorLight;
        case ReleaseStepStatus.Inactive:
        case ReleaseStepStatus.Skipped:
            return antGrey.grey2;
        default:
            return antGrey.grey2;
    }
};

export const shouldShowStepEmptyState = (stepData?: ReleaseStepCellData): boolean => {
    const pendingChecklistCount = stepData?.pendingChecklistItemCount ?? 0;
    const hasStatusReason = Boolean(stepData?.statusReasonString);
    return !stepData || (pendingChecklistCount === 0 && !hasStatusReason);
};

interface ReleaseStepCellProps {
    stepData?: ReleaseStepCellData;
}

export const ReleaseStepCell: React.FC<ReleaseStepCellProps> = ({ stepData }) => {
    const pendingChecklistCount = stepData?.pendingChecklistItemCount ?? 0;

    if (shouldShowStepEmptyState(stepData)) {
        return <EmptyCell />;
    }

    const truncatedDescription =
        stepData.statusReasonString.length > 20 ?
            `${stepData.statusReasonString.substring(0, 20)}...`
        :   stepData.statusReasonString;

    const isBetaTesting = stepData.type === 'betaTesting';
    const tooltipContent =
        isBetaTesting ?
            <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{stepData.name}</div>
                <div>{stepData.statusReasonString}</div>
            </div>
        :   stepData.statusReasonString;

    return (
        <Tooltip title={tooltipContent}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {pendingChecklistCount > 0 && (
                    <Tag
                        style={{
                            color: antGrey.grey1,
                            fontSize: 10,
                            backgroundColor: antGrey.grey7,
                            margin: 0,
                            borderRadius: '50%',
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                        }}
                    >
                        {pendingChecklistCount}
                    </Tag>
                )}
                <Text style={{ color: antGrey.grey8, fontSize: 14 }}>{truncatedDescription}</Text>
            </div>
        </Tooltip>
    );
};
